import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { Badge } from "primereact/badge";
import { Calendar } from "primereact/calendar";
import { FormInput } from "../../../components/FormInput";
import { FormTextarea } from "../../../components/FormTextarea";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import {
  F1010312Before,
  UpdatePlanOrgPlaceSeq,
  getCompanyPlaceList,
  getPlanOrganizationalBoundaryList,
} from "../../../utility/service/carbon-service";
import {
  checkDpSuccess,
  getFormErrorMessage,
  setformate,
  Base64Encoder,
  BcryptEncoder,
} from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { isGeneral, isManufacturing } from "../constants/inventoryType";
import { getSubItemsByItemNo } from "../../../utility/service/api-service";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import PrimeConfirmDialog from "../../../components/dialog/PrimeConfirmDialog";

const BoundaryItem = ({
  index,
  isEditable,
  subAction,
  remove,
  companyPlaceList,
  setValidPlanForm,
  register,
  errors,
  control,
  watch,
  clearErrors,
  item,
  append,
  sitePurposeList,
  planType,
}) => {
  const { t } = useTranslation();
  const [showPlaceSeqDialog, setShowPlaceSeqDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const placeSeqValue = watch(`placeList.${index}.placeSeq`);
  // 以該筆 placeSeq 對照 f1010201(companyPlaceList) 取得對應場所完整資訊
  const placeInfo = companyPlaceList?.find((p) => p.placeSeq === placeSeqValue);

  const renderPlaceInfoRow = (label, value, type = "text", fullRow = false) => {
    if (value == null || value === "") return null;
    const colClass = fullRow ? "col-12 flex flex-column gap-1 mb-2" : "col-12 md:col-6 flex flex-column gap-1 mb-2";
    return (
      <div key={label || String(value)} className={colClass}>
        {label && <label className="text-color-secondary text-sm font-medium">{label}</label>}
        {type === "textarea" ? (
          <textarea
            className="p-inputtextarea p-inputtext p-component w-full"
            value={String(value)}
            readOnly
            rows={3}
          />
        ) : (
          <span className="p-inputtext p-component w-full">{String(value)}</span>
        )}
      </div>
    );
  };

  const isNotBlank = (value) => value !== undefined && value !== null && value !== "";

  return (
    <div className="card p-3 surface-border border-1 border-round-md relative bg-gray-50">
      <div className="flex justify-content-between align-items-center mb-3 pb-2 border-bottom-1 surface-border">
        <Badge value={index + 1} />

        <span className="flex align-items-center gap-3">
          <Button
            icon="pi pi-info-circle"
            className="p-button-rounded p-button-text p-button-secondary"
            style={{ height: "25px", width: "25px" }}
            onClick={() => setShowPlaceSeqDialog(true)}
            type="button"
          />
          {subAction !== "detail" && isEditable && (
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                style={{ height: "25px", width: "25px" }}
                onClick={() => {
                  if (item?.planPlaceSeq) {
                    setShowDeleteConfirm(true);
                  } else {
                    remove(index);
                    if (watch("placeList")?.length <= 0) {
                      append({ placeSeq: "" });
                    }
                  }
                }}
                type="button"
              />
          )}
        </span>
      </div>

      <div className="grid">
        {/* 場所 */}
        <div className="col-12 lg:col-4">
          <label htmlFor={`placeList.${index}.placeSeq`} className="form-label required">
            {t("place_name")}
          </label>
          <Controller
            name={`placeList.${index}.placeSeq`}
            rules={setValidPlanForm("placeSeq")}
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                disabled={!isEditable || subAction === "detail" || !!item?.planPlaceSeq}
                appendTo={"self"}
                optionLabel="placeName"
                optionValue="placeSeq"
                value={field.value}
                options={companyPlaceList}
                placeholder={t("plz_chs")}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(e) => {
                  field.onChange(e.value);
                  // 場所選擇後，自動帶入對應地址到畫面上的地址欄位（已暫時註解）
                  // const selectedPlace = companyPlaceList?.find((place) => place.placeSeq === e.value);
                  // const address = selectedPlace?.address || "";
                  // setValue(`placeList.${index}.excludedEmissionAddress`, address, { shouldValidate: true });
                }}
              />
            )}
          />
        </div>

        <div className="col-12 lg:col-8 hidden lg:block"></div>

        {/* 站所用途 */}
        <div className="col-12 lg:col-4">
          <label htmlFor="sitePurposeId" className="form-label">
            {planType === "5" ? t("sitePurpose") : t("siteType")}
          </label>
          <Controller
            name={`placeList.${index}.sitePurposeId`}
            control={control}
            render={({ field, fieldState }) => (
              <Dropdown
                id={`placeList.${index}.sitePurposeId`}
                appendTo="self"
                optionLabel="subitemName"
                optionValue="subitemNo"
                disabled={!isEditable || subAction === "detail"}
                value={field.value ?? ""}
                options={sitePurposeList}
                placeholder={t("plz_chs")}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(e) => field.onChange(e.value ?? "")}
              />
            )}
          />
        </div>
        {/* 營業期間起日 */}
        <div className="col-12 lg:col-4">
          <label htmlFor={`placeList.${index}.businessStartDate`} className="form-label">
            {t("businessStartDate")}
          </label>
          <Controller
            name={`placeList.${index}.businessStartDate`}
            control={control}
            rules={{
              ...setValidPlanForm("businessStartDate"),
              validate: (val) => {
                if (
                  val !== "" &&
                  isNotBlank(watch(`placeList.${index}.businessEndDate`)) &&
                  watch(`placeList.${index}.businessEndDate`) < val
                )
                  return t("validation.startDate");
              },
            }}
            render={({ field, fieldState, formState }) => (
              <Calendar
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                dateFormat="yy-mm-dd"
                disabled={!isEditable || subAction === "detail"}
                value={field.value || ""}
                onChange={(e) => {
                  field.onChange(e.value);
                  if (e.value < watch(`placeList.${index}.businessEndDate`)) {
                    clearErrors(`placeList.${index}.businessEndDate`);
                  }
                }}
                showIcon
              />
            )}
          />
          {getFormErrorMessage(errors, `placeList.${index}.businessStartDate`)}
        </div>
        {/* 營業期間訖日 */}
        <div className="col-12 lg:col-4">
          <label htmlFor={`placeList.${index}.businessEndDate`} className="form-label">
            {t("businessEndDate")}
          </label>
          <Controller
            name={`placeList.${index}.businessEndDate`}
            control={control}
            rules={{
              ...setValidPlanForm("businessEndDate"),
              validate: (val) => {
                if (
                  val !== "" &&
                  isNotBlank(watch(`placeList.${index}.businessStartDate`)) &&
                  watch(`placeList.${index}.businessStartDate`) > val
                )
                  return t("validation.endDate");
              },
            }}
            render={({ field, fieldState, formState }) => (
              <Calendar
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                dateFormat="yy-mm-dd"
                disabled={!isEditable || subAction === "detail"}
                value={field.value || ""}
                onChange={(e) => {
                  field.onChange(e.value);
                  if (e.value > watch(`placeList.${index}.businessStartDate`)) {
                    clearErrors(`placeList.${index}.businessStartDate`);
                  }
                }}
                showIcon
              />
            )}
          />
          {getFormErrorMessage(errors, `placeList.${index}.businessEndDate`)}
        </div>
        {/* 邊界內未納入計算之排放源 */}
        <div className="col-12 lg:col-4">
          <FormInput
            register={register}
            errors={errors}
            id={`placeList.${index}.excludedEmissionSources`}
            name={`placeList.${index}.excludedEmissionSources`}
            type="text"
            rules={setValidPlanForm("excludedEmissionSources")}
            labelText={t("excludedEmissionSources")}
            disabled={!isEditable || subAction === "detail"}
          />
        </div>
        {/* 地址敘述 */}
        <div className="col-12 lg:col-4">
          <FormInput
            register={register}
            errors={errors}
            id={`placeList.${index}.excludedEmissionAddress`}
            name={`placeList.${index}.excludedEmissionAddress`}
            type="text"
            rules={setValidPlanForm("excludedEmissionAddress")}
            labelText={t("excludedAddress")}
            disabled={!isEditable || subAction === "detail"}
          />
        </div>
        {/* 未納入計算之排放源原因說明 */}
        <div className="col-12 lg:col-4">
          <FormTextarea
            register={register}
            errors={errors}
            areaClass="w-full"
            id={`placeList.${index}.exclusionReasonDescription`}
            name={`placeList.${index}.exclusionReasonDescription`}
            rules={setValidPlanForm("exclusionReasonDescription")}
            labelText={t("exclusionReasonDescription")}
            placeholder={t("validation.limitMaxChar", { max: 500 })}
            rows={1}
            autoResize
            disabled={!isEditable || subAction === "detail"}
          />
        </div>
        {/* 備註
        <div className="col-12 lg:col-4 field">
          <FormTextarea
            register={register}
            errors={errors}
            areaClass="w-full"
            id={`placeList.${index}.excludedEmissionRemarks`}
            name={`placeList.${index}.excludedEmissionRemarks`}
            labelText={t("remark")}
            rules={setValidPlanForm("excludedEmissionRemarks")}
            placeholder={t("validation.limitMaxChar", { max: 500 })}
            rows={1}
            autoResize
            disabled={!isEditable || subAction === "detail"}
          />
        </div> */}
      </div>

      <PrimeDialog
        visible={showPlaceSeqDialog}
        onClose={() => setShowPlaceSeqDialog(false)}
        // 純顯示視窗，不需要確認按鈕
        onConfirm={() => setShowPlaceSeqDialog(false)}
        title={t("organizational_boundary")}
        size="md"
        isShowConfBtn={false}
        isShowCancelBtn={false}
        bodyTemplate={
          <div className="grid">
            {!placeInfo ? (
              <p className="text-color-secondary">
                {t("plz_chs")} {t("place_name")}
              </p>
            ) : (
              <>
                {renderPlaceInfoRow(t("place_name"), placeInfo.placeName)}
                {renderPlaceInfoRow(t("address"), placeInfo.address)}
                {renderPlaceInfoRow(t("control_no"), placeInfo.registrationNo)}
                {renderPlaceInfoRow(t("factory_registration_no"), placeInfo.facRegNo)}
                {renderPlaceInfoRow(t("placeNo"), placeInfo.placeNo)}
                {renderPlaceInfoRow(t("taxIdNum"), placeInfo.taxIdNum)}
                {renderPlaceInfoRow(t("contact_name"), placeInfo.contactName)}
                {renderPlaceInfoRow(t("contact_email"), placeInfo.contactEmail)}
                {renderPlaceInfoRow(t("contact_phone"), placeInfo.contactTel)}
                {renderPlaceInfoRow(t("contact_mobile"), placeInfo.contactMobile)}
                {renderPlaceInfoRow(t("customerNo"), placeInfo.customerNo)}
                {placeInfo.customerNoMissingReason === "4"
                  ? renderPlaceInfoRow(t("customerNoMissingReason"), placeInfo.customerNoMissingRemark)
                  : renderPlaceInfoRow(t("customerNoMissingReason"), placeInfo.customerNoMissingReasonName)}
                {renderPlaceInfoRow(t("remark"), placeInfo.remark, "textarea", true)}
              </>
            )}
          </div>
        }
        contentStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      />

      <PrimeConfirmDialog
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          remove(index);
          if (watch("placeList")?.length <= 0) {
            append({ placeSeq: "" });
          }
          setShowDeleteConfirm(false);
        }}
        title={t("button.delete")}
        bodyTemplate={
          <span className={`${item?.hasEmissionIdentify ?? false ? "font-bold text-red-600" : ""}`}>
            {item?.hasEmissionIdentify ?? false
              ? t("confirm_delete_organizational_boundary_with_identify")
              : t("confirm_delete_organizational_boundary")}
          </span>
        }
      />
    </div>
  );
};

const Boundary = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const [oriData, setOriData] = useState([]);
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const { itemObj, setItemObj, action } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [formRule, setFormRule] = useState([]); //表單驗證
  const [subAction, setSubAction] = useState(action === "edit" ? "update" : "detail");
  const [sitePurposeList, setSitePurposeList] = useState([]); //站所用途
  const isNoPermissionResponse = (res) => res?.data?.ResHeader?.rtnCode === "_0407";

  const {
    reset,
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const { fields: placeFields, append, remove } = useFieldArray({
    control,
    name: "placeList",
  });

  useEffect(() => {
    axios_getSubItemsByItemNo("SITE_PURPOSE", 49).then(setSitePurposeList);
    axios_getCreateBefore();
    axios_getCompanyPlaceList();
    axiot_getPlanOrganizationalBoundaryList();
  }, []);

  const axios_getSubItemsByItemNo = (itemNo, orderNo) => {
    return getSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  //建立前查詢form 規則
  const axios_getCreateBefore = () => {
    checkLoading(loading);
    F1010312Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item.type === "collection") {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidPlanForm = useCallback(
    (formId) => {
      if (setValidForm("planList")?.length > 0) {
        const tmp = setValidForm("planList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("planList")],
  );

  const isNotBlank = (value) => value !== undefined && value !== null && value !== "";

  const axiot_getPlanOrganizationalBoundaryList = () => {
    checkLoading(loading);
    getPlanOrganizationalBoundaryList({
      planSn: itemObj?.planSn,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.planList.map((item) => {
            return {
              ...item,
              businessStartDate: isNotBlank(item.businessStartDate) ? new Date(item.businessStartDate) : null,
              businessEndDate: isNotBlank(item.businessEndDate) ? new Date(item.businessEndDate) : null,
            };
          });
          setValue("placeList", data);

          if (data.length <= 0) {
            setValue("placeList", [{ placeSeq: "" }]);
          }
          setOriData(data);
        } else if (isNoPermissionResponse(res)) {
          setSubAction("detail");
          setValue("placeList", []);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  //組織邊界廠商清單
  const axios_getCompanyPlaceList = () => {
    checkLoading(loading);
    getCompanyPlaceList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.companyPlaceList;
          setCompanyPlaceList(data?.filter((item) => item.companySeq === itemObj?.companySeq)[0]?.placeList);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = (data) => {
    checkLoading(loading);

    const sitePurposeMap = new Map(sitePurposeList.map((v, i) => [v.subitemNo, v.index]));

    const reqBody = {
      planSn: itemObj?.planSn,
      planList: data.placeList.map((item) => {
        return {
          ...item,
          businessStartDate: setformate(item.businessStartDate, "YYYY/MM/DD"),
          businessEndDate: setformate(item.businessEndDate, "YYYY/MM/DD"),
          sitePurposeId: encoder(sitePurposeMap, item.sitePurposeId),
        };
      }),
    };
    UpdatePlanOrgPlaceSeq(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          setSubAction("detail");
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(2);
          setItemObj((pre) => {
            return {
              ...pre,
              placeName: companyPlaceList
                .filter((c) => watch("placeList").some((p) => p.placeSeq === c.placeSeq))
                .map((c) => c.placeName),
            };
          });
        } else if (isNoPermissionResponse(res)) {
          setSubAction("detail");
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-column gap-3">
        {placeFields.map((item, index) => {
          return (
            <BoundaryItem
              key={item.id}
              item={item}
              index={index}
              isEditable={isEditable}
              subAction={subAction}
              remove={remove}
              setValidPlanForm={setValidPlanForm}
              register={register}
              errors={errors}
              control={control}
              companyPlaceList={companyPlaceList}
              watch={watch}
              clearErrors={clearErrors}
              append={append}
              sitePurposeList={sitePurposeList.filter((item) =>
                itemObj?.planType === "5" ? !item.subitemNo.includes("X") : item.subitemNo.includes("X"),
              )}
              planType={itemObj?.planType}
            />
          );
        })}
        {/* 新增第二筆組織編階：清冊類型為 0 通用行業、1 製造業 時不顯示 */}
        {isEditable && subAction !== "detail" && !isGeneral(itemObj?.planType) && !isManufacturing(itemObj?.planType) && (
          <div className="col-12 flex justify-content-center">
            <Button
              type="button"
              label={t("button.add")}
              icon="pi pi-plus"
              className="p-button-outlined w-full border-dashed surface-border text-600 hover:var(--primary-color)"
              style={{ minHeight: "3rem" }}
              onClick={() =>
                append({
                  placeSeq: "",
                })
              }
            />
          </div>
        )}
      </div>
      {subAction === "detail" ? (
        <div className="col-12 gap-2 flex-all-center">
          <Button
            disabled={!isEditable}
            label={t("button.edit")}
            onClick={(e) => {
              e.preventDefault();
              setSubAction("update");
            }}
            type="button"
            size="small"
          />
        </div>
      ) : (
        <div className="col-12 gap-2 flex-all-center">
          <Button
            disabled={!(isValid && isDirty) || !isEditable}
            type="submit"
            label={t("button.update")}
            size="small"
          />
          <Button
            type="button"
            outlined
            size="small"
            label={t("button.cancel")}
            onClick={(e) => {
              clearErrors();
              reset({ placeList: oriData });
              setSubAction("detail");
            }}
          />
        </div>
      )}
    </form>
  );
};

export default Boundary;
